import React, { useEffect } from "react"
import Alert from "react-bootstrap/Alert"
import Form from "react-bootstrap/Form"
import Modal from "react-bootstrap/Modal"
import Spinner from "react-bootstrap/Spinner"
import Button from "../common/Button.jsx"
import Dropzone from "dropzone"

const DUPLICATE_MODES = ["SKIP", "CREATE", "REPLACE"]

function pluralize(count, singular, plural = `${singular}s`) {
    return count === 1 ? singular : plural
}

function formatImportSuccessSummary(result) {
    const created = Number(result?.created ?? 0)
    const replaced = Number(result?.replaced ?? 0)
    const skipped = Number(result?.skipped ?? 0)

    if (created === 0 && replaced === 0 && skipped === 0) {
        return "Import complete. No customers were added or updated."
    }

    if (created === 0 && replaced === 0) {
        return `Import complete. No customers were added or updated. ${skipped} ${pluralize(skipped, "duplicate was", "duplicates were")} skipped.`
    }

    if (created > 0 && replaced === 0 && skipped === 0) {
        return `Import complete. ${created} new ${pluralize(created, "customer was", "customers were")} created.`
    }

    if (replaced > 0 && created === 0 && skipped === 0) {
        return `Import complete. ${replaced} existing ${pluralize(replaced, "customer was", "customers were")} replaced.`
    }

    const actions = []
    if (created > 0) actions.push(`${created} created`)
    if (replaced > 0) actions.push(`${replaced} replaced`)
    const actionText = actions.join(", ")

    if (skipped > 0) {
        const skipText = `${skipped} ${pluralize(skipped, "duplicate was", "duplicates were")} skipped`
        return `Import complete. ${actionText}; ${skipText}.`
    }

    return `Import complete. ${actionText}.`
}

function formatErrorMessage(error) {
    if (!error) return "Unknown error"
    if (typeof error === "string") return error
    if (error instanceof Error) return error.message || "Unknown error"
    try {
        return JSON.stringify(error)
    } catch {
        return String(error)
    }
}

/**
 * @param {{
 *   show: boolean,
 *   onClose: () => void,
 *   api: any,
 *   app?: any,
 *   onImported?: () => void,
 *   onSuccess?: (message: string) => void,
 *   showFishTagPadding?: boolean,
 * }} props
 */
export default function ImportCustomersModal({
    show,
    onClose,
    api,
    onImported = () => { },
    onSuccess = () => { },
    showFishTagPadding = true,
}) {
    const [file, setFile] = React.useState(/** @type {File | null} */(null))
    const [isWorking, setIsWorking] = React.useState(false)
    const [padFishTags, setPadFishTags] = React.useState(true) // Issue 134: Give lodges an option to pad these so they sort as desired
    const [fishTagPaddingCount, setFishTagPaddingCount] = React.useState(2)
    const [preview, setPreview] = React.useState(null)
    const [error, setError] = React.useState("")
    const dzRef = React.useRef(/** @type {any} */(null))
    const previewRequestIdRef = React.useRef(0)

    React.useEffect(() => {
        if (!show) return
        setFile(null)
        setPreview(null)
        setError("")
        setIsWorking(false)
        setPadFishTags(true)
        setFishTagPaddingCount(2)
    }, [show])



    async function callImportCustomers(csvFile, dryRun, duplicates) {
        if (typeof api?.importCustomers !== "function") {
            throw new Error("api.importCustomers is not available")
        }
        if (showFishTagPadding) {
            return await api.importCustomers(csvFile, dryRun, duplicates, padFishTags ? fishTagPaddingCount : null)
        } else {
            return await api.importCustomers(csvFile, dryRun, duplicates)
        }
    }

    useEffect(() => {
        console.log("preview", preview)
    }, [preview])

    useEffect(() => {
        if (!show) return
        // Prevent Dropzone from auto-attaching to elements.
        Dropzone.autoDiscover = false

        // Avoid Bootstrap `.row` here (negative margins can overflow the modal).
        const previewTemplate = `
          <div class="d-flex w-100 py-3 px-3 bg-light rounded align-items-center" style="box-sizing:border-box;">
            <div class="flex-grow-1" style="min-width:0;">
              <div class="name fw-semibold" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%;" data-dz-name></div>
              <strong class="error text-danger" data-dz-errormessage></strong>
            </div>
            <div class="ms-3 text-secondary" style="white-space:nowrap;">
              <div class="size" data-dz-size></div>
            </div>
            <div class="ms-3" style="white-space:nowrap;">
              <button data-dz-remove class="btn btn-light btn-sm">Remove</button>
            </div>
          </div>
        `

        const dz = new Dropzone("#import-customers-dropzone", {
            autoProcessQueue: false,
            url: "/api", // unused; we upload via api.importCustomers
            maxFiles: 1,
            addRemoveLinks: false,
            clickable: "#import-customers-dropzone-clickable",
            previewsContainer: "#import-customers-dropzone-preview",
            previewTemplate,
            acceptedFiles: ".csv,.xlsx,.xls",
        })

        async function previewFile(nextFile) {
            const requestId = ++previewRequestIdRef.current
            setIsWorking(true)
            setError("")
            setPreview(null)
            try {
                const res = await callImportCustomers(nextFile, true, "SKIP")
                if (requestId !== previewRequestIdRef.current) return
                setPreview(res ?? null)
            } catch (e) {
                if (requestId !== previewRequestIdRef.current) return
                setError(formatErrorMessage(e))
                setPreview(null)
            } finally {
                if (requestId !== previewRequestIdRef.current) return
                setIsWorking(false)
            }
        }

        dz.on("addedfile", (nextFile) => {
            if (!nextFile) return
            setFile(nextFile)
            setPreview(null)
            setError("")
            previewFile(nextFile)
        })
        dz.on("removedfile", () => {
            if (dz.files.length === 0) {
                setFile(null)
            }
            setPreview(null)
            setError("")
            previewRequestIdRef.current += 1
            setIsWorking(false)
        })

        dzRef.current = dz
        return () => {
            try {
                dz.destroy()
            } catch {
                // ignore
            }
            dzRef.current = null
        }
    }, [show])

    async function runSave(duplicates) {
        if (!file) return
        if (!DUPLICATE_MODES.includes(duplicates)) return
        setIsWorking(true)
        setError("")
        try {
            const result = await callImportCustomers(file, false, duplicates)
            onSuccess?.(formatImportSuccessSummary(result))
            onImported?.()
            onClose()
        } catch (e) {
            setError(formatErrorMessage(e))
        } finally {
            setIsWorking(false)
        }
    }

    const duplicates = Number(preview?.duplicates ?? 0)
    const valid = Number(preview?.valid ?? 0)
    const created = Number(preview?.created ?? 0)
    const replaced = Number(preview?.replaced ?? 0)
    const skipped = Number(preview?.skipped ?? 0)
    const errors = Array.isArray(preview?.errors) ? preview.errors : []

    const canSave = Boolean(file) && !isWorking && valid > 0

    return (
        <Modal show={show} onHide={onClose} centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Import Customers</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {error ? (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                ) : null}

                <div className="mb-3">
                    <div
                        id="import-customers-dropzone"
                        className={file ? "" : "p-3"}
                        style={{
                            border: "1px dashed #aaa",
                            backgroundColor: "white",
                            borderRadius: 6,
                        }}
                    >
                        <div
                            id="import-customers-dropzone-clickable"
                            className={file ? "d-none" : "py-5 text-center"}
                            style={{ cursor: isWorking ? "not-allowed" : "pointer" }}
                        >
                            <div className="fw-semibold">Browse Files</div>
                            <div className="text-secondary" style={{ fontSize: "0.875rem" }}>
                                Drag and drop a CSV or Excel file here
                            </div>
                        </div>
                        <div
                            id="import-customers-dropzone-preview"
                            style={{ maxWidth: "100%", overflowX: "hidden" }}
                        />
                    </div>
                    <div className="text-muted mt-2" style={{ fontSize: "0.875rem" }}>
                        Upload a CSV or Excel file exported from your customer spreadsheet.
                    </div>
                    {showFishTagPadding ? (
                        <div className="mt-2">
                            <Form.Check className="d-flex align-items-center gap-2 mb-0 ps-0" style={{ minHeight: 0 }}>
                                <Form.Check.Input id="import-customers-pad-fish-tags" className="mt-0 ms-0 flex-shrink-0" style={{ float: "none" }} checked={padFishTags} onChange={e => setPadFishTags(e.currentTarget.checked)} />
                                <Form.Check.Label htmlFor="import-customers-pad-fish-tags" className="mb-0 ps-0" style={{ fontSize: "0.875rem" }}>
                                    Pad all Fish Tags to
                                </Form.Check.Label>
                                <Form.Control id="import-customers-pad-fish-tags-digits" type="number" min={2} step={1} value={fishTagPaddingCount} className="flex-grow-0" style={{ width: "8ch", fontSize: "0.875rem" }}
                                    onChange={e => { setFishTagPaddingCount(Math.max(Math.trunc(Number(e.currentTarget.value)), 2)) }}
                                />
                                <Form.Check.Label htmlFor="import-customers-pad-fish-tags" className="mb-0 ps-0" style={{ fontSize: "0.875rem" }}>
                                    digits.
                                </Form.Check.Label>
                            </Form.Check>
                        </div>
                    ) : null}
                </div>

                {preview ? (
                    <div className="mb-2">
                        <div className="text-secondary" style={{ fontSize: "0.875rem" }}>
                            Valid: <strong>{valid}</strong> · Duplicates: <strong>{duplicates}</strong>
                        </div>
                        {errors.length ? (
                            <Alert variant="warning" className="mt-2 mb-0">
                                <div className="fw-semibold mb-1">Errors</div>
                                <ul className="mb-0">
                                    {errors.slice(0, 10).map((msg, idx) => (
                                        <li key={idx}>{String(msg)}</li>
                                    ))}
                                </ul>
                            </Alert>
                        ) : null}
                    </div>
                ) : null}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <div className="d-flex align-items-center gap-2">
                    {isWorking ? (
                        <>
                            <Spinner size="sm" />
                            <span className="text-secondary" style={{ fontSize: "0.875rem" }}>
                                Working…
                            </span>
                        </>
                    ) : null}
                </div>
                <div className="d-flex gap-2 flex-wrap justify-content-end" style={{ maxWidth: "100%" }}>
                    <Button variant="secondary" onClick={onClose} disabled={isWorking}>
                        Cancel
                    </Button>
                    {preview && duplicates > 0 ? (
                        <>
                            <Button variant="custom" size="sm" onClick={() => runSave("CREATE")} disabled={!canSave}>
                                Create All
                            </Button>
                            <Button variant="custom" size="sm" onClick={() => runSave("REPLACE")} disabled={!canSave}>
                                Replace Matches
                            </Button>
                            <Button variant="custom" size="sm" onClick={() => runSave("SKIP")} disabled={!canSave}>
                                Skip Matches
                            </Button>
                        </>
                    ) : (
                        <Button variant="custom" onClick={() => runSave("SKIP")} disabled={!canSave}>
                            Save
                        </Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    )
}

